// Rapportvakt — one delivery rendered as a digest pane.
//
// One point is exactly two things: a full sentence that stands on its own and
// the citation bar with a short reference and a link. Nothing else.

import React from 'react';
import { useRvLang } from './Shell';
import { Glyph } from './Glyph';
import { publisherLogo } from '../../config/rapportvakt';
import { publisherInitials } from './strings';
import { evaluatePoint } from './visualRules';
import { PointVisual } from './PointVisual';
import { citationRefText } from './citationRef';

// Re-exported so every existing `import { Glyph } from './Delivery'` keeps
// working; the glyph lives in its own module because Shell.jsx needs it too
// and this file imports useRvLang from Shell — the two must not import each
// other.
export { Glyph };

/** Publisher logo, falling back to a two-letter monogram if the file is gone. */
export function PublisherMark({ publisher }) {
  const src = publisherLogo(publisher);
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return (
      <span className="rv-chip" aria-hidden="true">
        {publisherInitials(publisher)}
      </span>
    );
  }
  return (
    <span className="rv-chip rv-chip--logo">
      <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
}


function Point({ point, index, evaluation }) {
  const { t } = useRvLang();
  const { headline, impact, lead, hero, visual, quote, citation, citationShort } = point;
  // Collapsed by default (owner decision 2026-09-11): the quote used to sit
  // open in a large box on every point; now it only shows once the reader
  // asks for it via the toggle below.
  const [quoteOpen, setQuoteOpen] = React.useState(false);

  // A slim automated point (owner decision 2026-09-09) has no `hero` number
  // and no `visual` chart spec - PointVisual renders nothing for it, and
  // without this the headline the model wrote would never be shown at all.
  // A catalogue point always carries `hero`, so checking "no hero and no
  // visual" (rather than just "has a headline") keeps catalogue points
  // rendering exactly as before, headline included where PointVisual/hero
  // already show it.
  const isSlim = Boolean(headline) && !hero && !visual;
  const quoteId = `rv-quote-${point.id}`;

  return (
    <div className="rv-pt">
      <div className="rv-pt__head">
        <span className="rv-pt__num" aria-hidden="true">
          {index + 1}
        </span>
        {isSlim ? <h3 className="rv-pt__headline">{headline}</h3> : null}
        <p className="rv-pt__lead">
          {impact ? (
            <>
              <strong className="rv-pt__impact">{impact}:</strong> {lead}
            </>
          ) : (
            lead
          )}
        </p>

        <PointVisual point={point} evaluation={evaluation} />

        {/* One block: a compact row, always visible, with the toggle (quote
            points only), the page/section reference, then the link - in that
            order, so the link stays flush right on a line that fits at 660px
            and wraps without losing the link on a phone - and, when the
            reader opens it, the quote as the lower part of the same box. */}
        <div className="rv-cite">
          <div className="rv-cite__row">
            {quote ? (
              <button
                type="button"
                className="rv-cite__toggle"
                aria-expanded={quoteOpen}
                aria-controls={quoteId}
                aria-label={quoteOpen ? t.hideQuote : t.showQuote}
                onClick={() => setQuoteOpen((open) => !open)}
              >
                <span
                  className={`rv-cite__chevron${quoteOpen ? ' rv-cite__chevron--open' : ''}`}
                  aria-hidden="true"
                >
                  <Glyph name="chevron" size={11} />
                </span>
                {t.referens}
              </button>
            ) : null}
            <span className="rv-cite__ref">{citationRefText(citationShort, Boolean(quote))}</span>
            <a
              className="rv-cite__link"
              href={citation.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.link}
              {quote ? <Glyph name="arrow" size={12} /> : null}
            </a>
          </div>

          {quote ? (
            <blockquote id={quoteId} className="rv-cite__quote" hidden={!quoteOpen}>
              <p>”{quote}”</p>
            </blockquote>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The digest pane: report header, then the three points.
 *
 * Each point earns its own visual (or doesn't) purely on its own merits —
 * there is no delivery-wide competition or quota. `evaluatePoint` runs once
 * per point, independently of its siblings; see the file-level note in
 * visualRules.js. */
export function Delivery({ delivery }) {
  const { t } = useRvLang();
  return (
    <article className="rv-pane">
      <header className="rv-pane__head">
        <PublisherMark publisher={delivery.publisher} />
        <div className="rv-pane__titles">
          <p className="rv-pane__kicker">
            {delivery.publisher}, {delivery.dateLabel}
          </p>
          <h2 className="rv-pane__title">{delivery.title}</h2>
        </div>
        <a
          className="rv-pane__link"
          href={delivery.reportPageUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.reportPage}
        </a>
      </header>

      {delivery.points.map((point, index) => (
        <Point point={point} index={index} evaluation={evaluatePoint(point)} key={point.id} />
      ))}
    </article>
  );
}
